import { useCallback, useEffect, useRef } from 'react'

interface UseInfiniteScrollOptions {
  /** Whether there are more pages available to load */
  hasNextPage: boolean
  /** Whether a request to fetch the next page is currently in progress */
  isFetchingNextPage: boolean
  /** Function to call when the next page should be loaded */
  fetchNextPage: () => void
  /**
   * The percentage of the target element that must be visible before triggering the callback.
   * Value between 0 and 1. Default is 0.1 (10%).
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#threshold
   */
  threshold?: number
  /**
   * Margin around the root element's bounding box. Can be used to trigger loading
   * before the target element actually enters the viewport.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#rootmargin
   */
  rootMargin?: string
}

/**
 * A custom React hook that provides infinite scroll functionality using the Intersection Observer API.
 *
 * This hook automatically loads more content when the user scrolls near a target element,
 * providing a smooth infinite scrolling experience. It handles the intersection observation
 * logic and provides proper cleanup.
 *
 * @returns A React ref callback that should be attached to the DOM element that triggers loading when visible
 *
 */
export function useInfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  threshold = 0.1,
  rootMargin = '100px',
}: UseInfiniteScrollOptions) {
  const observerRef = useRef<IntersectionObserver | null>(null)

  const hasNextPageRef = useRef(hasNextPage)
  const isFetchingNextPageRef = useRef(isFetchingNextPage)
  const fetchNextPageRef = useRef(fetchNextPage)
  useEffect(() => { hasNextPageRef.current = hasNextPage }, [hasNextPage])
  useEffect(() => { isFetchingNextPageRef.current = isFetchingNextPage }, [isFetchingNextPage])
  useEffect(() => { fetchNextPageRef.current = fetchNextPage }, [fetchNextPage])

  return useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    if (!node) return

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (
          entry?.isIntersecting &&
          hasNextPageRef.current &&
          !isFetchingNextPageRef.current
        ) {
          fetchNextPageRef.current()
        }
      },
      { threshold, rootMargin }
    )
    observerRef.current.observe(node)

    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [threshold, rootMargin])
}
